var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable, reaction, when } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
export class AdminPatientSettingsViewModel {
    constructor(container) {
        this.healthExPreferredContactsOptions = [ContactMethod.EMAIL, ContactMethod.TEXT];
        this.discardCancelDialog = new SimpleDialogModel();
        this.discardLeaveDialog = new SimpleDialogModel();
        this.learnMoreDialog = new SimpleDialogModel();
        this.patientAuthenticationFormModel = new FormModel({
            usePatientPortalAuthentication: false,
            forcePatientPortalEmbed: false,
            patientPortalWebLaunchUrl: "",
            patientPortalMobileAppLaunchUrl: "",
            patientPortalIosAppInstallUrl: "",
            patientPortalAndroidAppInstallUrl: "",
            useClearAuthentication: false,
        }, ["patientPortalWebLaunchUrl", "patientPortalMobileAppLaunchUrl"]);
        this.videoUploadingInfo = null;
        this.isPatientAuthenticationExpanded = false;
        this.previewConsentDialogOpen = false;
        this.authManager = container.authManager;
        this.consentConfigManager = container.consentConfigManager;
        this.adminUIManager = container.adminUIManager;
        this.routingUIManager = container.routingUIManager;
        this.organizationsManager = container.organizationsManager;
        reaction(() => this.adminUIManager.sectionToExpand, async () => {
            await this.expandSectionIfNecessary();
        }, { fireImmediately: true });
        this.routingUIManager.registerUnloadHandler({
            needed: () => this.discardConfirmationNeeded,
            confirmation: () => this.showDiscardLeaveDialog(),
        });
    }
    run() { }
    stop() { }
    get organization() {
        if (this.organizationsManager.myOrganization.value === NOT_LOADED) {
            return NOT_LOADED;
        }
        if (this.organizationsManager.myOrganization.value === null) {
            return null;
        }
        if (this.organizationsManager.myOrganization.value.type !== UserType.HEALTHCARE_ORGANIZATION) {
            return null;
        }
        return this.organizationsManager.myOrganization.value;
    }
    get consentConfig() {
        if (this.consentConfigManager.consentConfig.value === NOT_LOADED || !this.consentConfigManager.consentConfig.value) {
            return NOT_LOADED;
        }
        return this.consentConfigManager.consentConfig.value;
    }
    get showSetupPatientAuthenticationBanner() {
        if (this.organization === NOT_LOADED || !this.organization)
            return false;
        return this.organization.patientAuthenticationProviders.length === 0;
    }
    get authenticationOptionsEnabled() {
        return !this.patientAuthenticationFormModel.input.forcePatientPortalEmbed;
    }
    get patientPortalLaunchUrlsEnabled() {
        return this.patientAuthenticationFormModel.input.forcePatientPortalEmbed;
    }
    get isPatientAuthenticationDirty() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return (this.patientAuthenticationFormModel.input.usePatientPortalAuthentication !==
            this.patientAuthenticationIsEnabled(GFIDProvider.SMART_ON_FHIR),
            this.patientAuthenticationFormModel.input.useClearAuthentication !== this.patientAuthenticationIsEnabled(GFIDProvider.CLEAR));
    }
    get savePatientAuthenticationText() {
        return "Save";
    }
    get orgName() {
        return this.authManager.orgName;
    }
    get patientPortalName() {
        var _a, _b;
        if (this.organization === NOT_LOADED)
            return "patient";
        return (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.patientPortalName) !== null && _b !== void 0 ? _b : "patient";
    }
    patientAuthenticationIsEnabled(provider) {
        if (this.organization === NOT_LOADED || this.organization === null)
            return false;
        return this.organization.patientAuthenticationProviders.includes(provider);
    }
    async expandSectionIfNecessary() {
        if (this.adminUIManager.sectionToExpand === "PATIENT_AUTHENTICATION") {
            await this.togglePatientAuthenticationExpanded();
        }
    }
    async togglePatientAuthenticationExpanded() {
        if (this.discardConfirmationNeeded) {
            const confirmed = await this.showDiscardCancelDialog();
            if (!confirmed)
                return;
        }
        this.isPatientAuthenticationExpanded = !this.isPatientAuthenticationExpanded;
        if (this.isPatientAuthenticationExpanded) {
            await this.loadPatientAuthentication();
        }
    }
    get discardConfirmationNeeded() {
        if (this.isPatientAuthenticationExpanded && this.isPatientAuthenticationDirty) {
            return true;
        }
        return false;
    }
    async loadPatientAuthentication() {
        var _a, _b, _c, _d;
        await when(() => this.consentConfig !== NOT_LOADED);
        await when(() => this.organization !== NOT_LOADED);
        if (this.consentConfig === NOT_LOADED || this.organization === NOT_LOADED || this.organization === null)
            return;
        this.patientAuthenticationFormModel.updateInput({
            usePatientPortalAuthentication: this.patientAuthenticationIsEnabled(GFIDProvider.SMART_ON_FHIR),
            useClearAuthentication: this.patientAuthenticationIsEnabled(GFIDProvider.CLEAR),
            forcePatientPortalEmbed: this.organization.forcePatientPortalEmbed,
            patientPortalWebLaunchUrl: (_a = this.organization.patientPortalWebLaunchUrl) !== null && _a !== void 0 ? _a : "",
            patientPortalMobileAppLaunchUrl: (_b = this.organization.patientPortalMobileAppLaunchUrl) !== null && _b !== void 0 ? _b : "",
            patientPortalIosAppInstallUrl: (_c = this.organization.patientPortalIosAppInstallUrl) !== null && _c !== void 0 ? _c : "",
            patientPortalAndroidAppInstallUrl: (_d = this.organization.patientPortalAndroidAppInstallUrl) !== null && _d !== void 0 ? _d : "",
        });
    }
    async savePatientAuthentication() {
        this.patientAuthenticationFormModel.clearErrors();
        const input = this.patientAuthenticationFormModel.input;
        if (!input.usePatientPortalAuthentication && !input.useClearAuthentication) {
            this.patientAuthenticationFormModel.addError("usePatientPortalAuthentication", "Please select at least one authentication option to allow patients to access HealthEx.");
        }
        if (input.forcePatientPortalEmbed) {
            this.patientAuthenticationFormModel.validateField("patientPortalWebLaunchUrl", validators.notBlank, "Enter your patient portal web launch URL");
            this.patientAuthenticationFormModel.validateField("patientPortalMobileAppLaunchUrl", validators.notBlank, "Enter your patient portal mobile app launch URL");
        }
        if (this.patientAuthenticationFormModel.hasErrors()) {
            return;
        }
        const patientAuthenticationUpdate = this.getPatientAuthenticationUpdates();
        if (patientAuthenticationUpdate) {
            const showSetupPatientAuthenticationBannerWas = this.showSetupPatientAuthenticationBanner;
            await this.adminUIManager.updatePatientSettingsPatientAuthentication(this.getPatientAuthenticationUpdates());
            if (showSetupPatientAuthenticationBannerWas && !this.showSetupPatientAuthenticationBanner) {
                this.adminUIManager.openGoLiveChecklistDialogModel();
            }
        }
        this.isPatientAuthenticationExpanded = false;
    }
    restoreAllDefaultsPatientAuthentication() {
        this.patientAuthenticationFormModel.updateInput({
            usePatientPortalAuthentication: false,
            useClearAuthentication: false,
            forcePatientPortalEmbed: false,
            patientPortalWebLaunchUrl: "",
            patientPortalMobileAppLaunchUrl: "",
            patientPortalIosAppInstallUrl: "",
            patientPortalAndroidAppInstallUrl: "",
        });
    }
    getPatientAuthenticationUpdates() {
        if (this.organization === NOT_LOADED || this.organization === null)
            return null;
        const input = this.patientAuthenticationFormModel.input;
        const patientPortalWebLaunchUrl = input.patientPortalWebLaunchUrl === "" ? null : input.patientPortalWebLaunchUrl;
        const patientPortalMobileAppLaunchUrl = input.patientPortalMobileAppLaunchUrl === "" ? null : input.patientPortalMobileAppLaunchUrl;
        const patientPortalIosAppInstallUrl = input.patientPortalIosAppInstallUrl === "" ? null : input.patientPortalIosAppInstallUrl;
        const patientPortalAndroidAppInstallUrl = input.patientPortalAndroidAppInstallUrl === "" ? null : input.patientPortalAndroidAppInstallUrl;
        if (input.usePatientPortalAuthentication === this.patientAuthenticationIsEnabled(GFIDProvider.SMART_ON_FHIR) &&
            input.useClearAuthentication === this.patientAuthenticationIsEnabled(GFIDProvider.CLEAR) &&
            input.forcePatientPortalEmbed === this.organization.forcePatientPortalEmbed &&
            patientPortalWebLaunchUrl === this.organization.patientPortalWebLaunchUrl &&
            patientPortalMobileAppLaunchUrl === this.organization.patientPortalMobileAppLaunchUrl &&
            patientPortalIosAppInstallUrl === this.organization.patientPortalIosAppInstallUrl &&
            patientPortalAndroidAppInstallUrl === this.organization.patientPortalAndroidAppInstallUrl) {
            return null;
        }
        const patientAuthenticationProviders = [];
        if (input.usePatientPortalAuthentication) {
            patientAuthenticationProviders.push(GFIDProvider.SMART_ON_FHIR);
        }
        if (input.useClearAuthentication) {
            patientAuthenticationProviders.push(GFIDProvider.CLEAR);
        }
        return {
            type: UserType.HEALTHCARE_ORGANIZATION,
            patientAuthenticationProviders,
            forcePatientPortalEmbed: input.forcePatientPortalEmbed,
            patientPortalWebLaunchUrl,
            patientPortalMobileAppLaunchUrl,
            patientPortalIosAppInstallUrl,
            patientPortalAndroidAppInstallUrl,
        };
    }
    getPreferredContactLabel(value) {
        return value === "TEXT" ? "Text (SMS)" : "Email (default)";
    }
    async showDiscardCancelDialog() {
        return await this.discardCancelDialog.showAndWait();
    }
    async showDiscardLeaveDialog() {
        return await this.discardLeaveDialog.showAndWait();
    }
    showLearnMoreDialog() {
        this.learnMoreDialog.open = true;
    }
}
__decorate([
    observable
], AdminPatientSettingsViewModel.prototype, "videoUploadingInfo", void 0);
__decorate([
    observable
], AdminPatientSettingsViewModel.prototype, "isPatientAuthenticationExpanded", void 0);
__decorate([
    observable
], AdminPatientSettingsViewModel.prototype, "previewConsentDialogOpen", void 0);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "organization", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "consentConfig", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "showSetupPatientAuthenticationBanner", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "authenticationOptionsEnabled", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "patientPortalLaunchUrlsEnabled", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "isPatientAuthenticationDirty", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "savePatientAuthenticationText", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "orgName", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "patientPortalName", null);
__decorate([
    computed
], AdminPatientSettingsViewModel.prototype, "discardConfirmationNeeded", null);
